// The route tree. One source, so §2's screen map and this cannot drift.
//
// C7 resolved in favour of the S8B §6 shell over the deck's rails, on founder
// sign-off. The deck draws four different rails across its five boards and none
// of them carries Attention — the product's operating centre and post-login
// destination (F02). The divergence is registered in docs/qe-ui-exceptions.md
// rather than being quietly implemented.
//
// A nav entry appears here only if it has a route. An earlier design listed
// Settings, Help and an account menu in the rail with nothing behind them,
// which ships dead controls on ~40 of 46 screens from Sprint 0 onward.

export type Screen = {
  /** 9B2-B{board}-{nn}, the traceability ID everything else keys off */
  id: string;
  path: string;
  label: string;
  /** absent when the screen is a state of another screen rather than a route */
  nav?: 'global' | 'property' | 'inspector';
};

/** Reached without a property in context. */
export const GLOBAL: Screen[] = [
  // B1-02, not B1-01. B1-01 is the login screen at /login, and this list
  // carried its id for ten sprints — a traceability defect nobody could see
  // while both screens were unbuilt. Login now exists and needs its own id
  // back; the portfolio has always been B1-02.
  { id: 'B1-02', path: '/', label: 'Portfolio', nav: 'global' },
  { id: 'B3-17', path: '/my-work', label: 'My Work', nav: 'global' },
  // Dropped from this list by a careless edit in Sprint 9 and restored here.
  // Nothing noticed for a sprint because the screen behind it was still a
  // placeholder — which is the argument for building the screens: a real one
  // that vanishes is reported, a placeholder that vanishes is not.
  { id: 'B3-24', path: '/admin/users', label: 'People', nav: 'global' },
  { id: 'B5-01', path: '/search', label: 'Search', nav: 'global' },
  { id: 'B3-25', path: '/notifications', label: 'Notifications', nav: 'global' },
];

/** Reached without a property and deliberately not in the rail.
 *
 *  A rail with seven items is the flat list S8B §6 supersedes, so these are
 *  reached from the screen they belong to: the My Work calendar from My Work,
 *  settings from an account menu, export from Admin.
 *
 *  `no-crop:` marks a route the 9B2 deck never drew. It is not a 9B2
 *  traceability ID and does not pretend to be one — borrowing a neighbouring
 *  screen's ID would put these under a visual baseline that was never approved
 *  for them. Each is listed in docs/qe-ui-exceptions.md and reviewed against
 *  S8B behavioural intent instead. */
export const GLOBAL_UNLISTED: Screen[] = [
  // Reached without a session at all, so they cannot be in a rail: the rail is
  // drawn from a role and there is none yet.
  { id: 'B1-01', path: '/login', label: 'Sign in' },
  { id: 'B5-08', path: '/reset', label: 'Reset your password' },
  { id: 'no-crop:my-work-calendar', path: '/my-work/calendar', label: 'My work calendar' },
  { id: 'B5-07', path: '/settings/notifications', label: 'Notification settings' },
  { id: 'B5-06', path: '/admin/export', label: 'Export' },
];

/** An inspector's entire shell. E12-S01 is "assigned work **and no other app
 *  areas**", so this is a separate list rather than GLOBAL with entries
 *  filtered out — a filtered list is one `||` away from showing everything,
 *  and the failure would be invisible until somebody looked at a real
 *  inspector's screen. */
export const INSPECTOR: Screen[] = [
  { id: 'B3-19', path: '/inspections', label: 'Inspections', nav: 'inspector' },
];

/** Reached inside /p/:propertyId. Attention is first because F02 makes it the
 *  landing destination — switching property always lands here, never on the
 *  screen you happened to be on for the previous one. */
export const PROPERTY: Screen[] = [
  { id: 'B1-03', path: 'attention', label: 'Attention', nav: 'property' },
  { id: 'B2-07', path: 'evidence', label: 'Evidence', nav: 'property' },
  { id: 'B2-13', path: 'obligations', label: 'Obligations', nav: 'property' },
  { id: 'B2-11', path: 'assets', label: 'Assets', nav: 'property' },
  { id: 'B3-15', path: 'actions', label: 'Actions', nav: 'property' },
  { id: 'B3-18', path: 'capital', label: 'Capital', nav: 'property' },
  { id: 'B1-06', path: 'health', label: 'Health', nav: 'property' },
  { id: 'B1-05', path: 'details', label: 'Details', nav: 'property' },
  { id: 'no-crop:calendar', path: 'calendar', label: 'Calendar', nav: 'property' },
  // B3-23 is the deck's Report / Output screen, drawn as a contextual modal.
  // C20 records why it is a route here instead.
  { id: 'B3-23', path: 'reports', label: 'Reports', nav: 'property' },
];

/** States of another screen, not routes of their own. Listed so the register
 *  check can tell "has no route" from "is deliberately not a route" — an
 *  earlier revision named four of these six and would have flagged two screens
 *  against an exemption that did not cover them. */
export const STATE_NOT_ROUTE = ['B4-04', 'B4-08', 'B4-09', 'B5-03', 'B5-05', 'B5-11'];

export const LANDING = 'attention';

/** The property level of the rail (founder, 2026-09-15, the prototype's rail).
 *
 *  Four destinations with icons. The other sections — Assets, Obligations,
 *  Capital, Health, Calendar, Reports, Inspections — are one step away on
 *  Property (B1-05), instead of a row of ten tabs. Attention stays first (F02). */
export const PROPERTY_RAIL: { id: string; path: string; label: string }[] = [
  { id: 'B1-03', path: 'attention', label: 'Attention' },
  { id: 'B1-05', path: 'details', label: 'Property' },
  { id: 'B2-07', path: 'evidence', label: 'Evidence' },
  { id: 'B3-15', path: 'actions', label: 'Actions' },
];

/** Which rail destination a property route belongs to, so a section reached
 *  from Property keeps Property lit and a document keeps Evidence lit. */
export function railSectionOf(subpath: string): string {
  const first = subpath.split('/')[0] ?? '';
  if (first === 'attention' || first === 'issues') return 'attention';
  if (first === 'evidence') return 'evidence';
  if (first === 'actions') return 'actions';
  return 'details';
}
